import { Component, computed, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ProjectAndTypesService } from '../services/projectandTypes/project-and-types.service';
import { SelectsData, SelectsService } from '../services/selectService/selects.service';
import { DateTime } from 'luxon';

interface SelectItem {
  label: string;
  value: string;
}


@Component({
  selector: 'app-select-bar',
  templateUrl: './select-bar.component.html',
  styleUrls: ['./select-bar.component.scss'],
  standalone: true,
  imports: [
    AutoCompleteModule,
    ButtonModule,
    DatePickerModule,
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    SelectModule,
    TextareaModule,
  ]
})
export class SelectBarComponent implements OnInit {

  private readonly projectAndTypes = inject(ProjectAndTypesService);
  private readonly selectService = inject(SelectsService);

  private readonly DestroyRef = inject(DestroyRef);

  // Signals for reactive state management
  public readonly projects = signal<string[]>([]);
  public readonly types = signal<string[]>([]);
  public readonly services = signal<string[]>([]);

  // Form controls for reactive forms
  private readonly fb = inject(FormBuilder);

  public readonly formGroup = this.fb.group({
    project: [''],
    type: [''],
    service: [''],
    boxId: [''],
    folderId: [''],
    message: [''],
    dateStart: [null],
    dateEnd: [null],
  });

  // We'll subscribe to formGroup.valueChanges in ngOnInit with debouncing

  constructor() {
    this.initializeSelectsData();
  }

  /**
   * Initialize selects data from service with proper subscription management
   */
  private initializeSelectsData(): void {
    const selectsData = this.selectService.fieldsValue();
    this.formGroup.patchValue(selectsData as any, { emitEvent: false });
  }

  ngOnInit(): void {
    this.loadProjectAndTypesData();

    // Subscribe to form changes and update the shared signal in a controlled way
    this.formGroup.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.DestroyRef)
      )
      .subscribe((formValue: Partial<SelectsData>) => {
        this.selectService.fieldsValue.set(formValue);
      });
  }

  /**
   * Load projects, types, and services data with proper error handling
   */
  private loadProjectAndTypesData(): void {
    this.projectAndTypes.uploadFromDataStore()
      .pipe(takeUntilDestroyed(this.DestroyRef))
      .subscribe({
        next: (data: any) => {
          this.updateProjects(data.projects);
          this.updateTypes(data.types);
          this.updateServices(data.services);
        },
        error: (error) => {
          console.error('Error loading project and types data:', error);
          // Set default empty arrays on error
        }
      });
  }

  /**
   * Update projects list with proper validation
   */
  private updateProjects(projectsData: Record<string, unknown> | undefined): void {
    const projArr = this.processDataArray(projectsData);
    this.projects.set(projArr);
  }

  /**
   * Update types list with proper validation
   */
  private updateTypes(typesData: Record<string, unknown> | undefined): void {
    const typesArr = this.processDataArray(typesData);
    this.types.set(typesArr);
  }

  /**
   * Update services list with proper validation
   */
  private updateServices(servicesData: Record<string, unknown> | undefined): void {
    const servicesArr = this.processDataArray(servicesData);
    this.services.set(servicesArr);
  }

  /**
   * Process data array with proper validation and sorting
   */
  private processDataArray(data: Record<string, unknown> | undefined): string[] {
    if (!data || typeof data !== 'object') {
      return ['All'];
    }

    const processedArray = Object.values(data)
      .map(String)
      .filter(Boolean)
      .sort();

    return processedArray;
  }
}
